import { useCallback, useEffect, useRef } from "react";

/* Colar link com Ctrl+V sem precisar clicar no campo primeiro.

   Nos pop-ups de MakerWorld o fluxo é sempre "copiei o link no navegador e
   vim colar aqui" — ter que acertar o clique no campo antes do Ctrl+V é um
   passo a mais sem motivo. Este hook escuta o evento `paste` da página e
   entrega o link pro campo mesmo com o foco em qualquer lugar do modal.

   Escuta `paste` em vez de `keydown` de propósito: pega Ctrl+V, Cmd+V,
   Shift+Insert, colar pelo botão direito e colar do teclado do Android, e já
   vem com o texto pronto (nada de ler a área de transferência na mão).

   Quando o foco JÁ está num campo de texto, não faz nada — o navegador cola
   sozinho, e interferir duplicaria o texto.

   Modais empilham (ex.: "Importar fotos" abre por cima da edição do produto),
   então só o hook ativado mais recentemente responde — senão o mesmo Ctrl+V
   cairia em dois campos ao mesmo tempo. */

const ehCampoDeTexto = (el) =>
  !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

/* Aproveita o link de dentro do que foi copiado: o usuário pode ter pegado o
   título junto, ou copiado sem o https://. Devolve "" quando não tem link. */
export function linkColado(texto) {
  const bruto = String(texto || "").trim();
  if (!bruto) return "";
  const comEsquema = bruto.match(/https?:\/\/\S+/i);
  if (comEsquema) return comEsquema[0];
  const semEsquema = bruto.match(/(?:www\.)?makerworld\.com\/\S*/i);
  return semEsquema ? "https://" + semEsquema[0] : "";
}

// Pilha de quem quer receber o Ctrl+V; o último a entrar é quem responde.
const pilha = [];
let ouvindo = false;

function aoColarNaPagina(evento) {
  const alvo = pilha[pilha.length - 1];
  if (!alvo || ehCampoDeTexto(evento.target)) return;
  const link = linkColado(evento.clipboardData?.getData("text"));
  if (!link) return;
  evento.preventDefault();
  alvo(link);
}

function registrar(handler) {
  pilha.push(handler);
  if (!ouvindo) { document.addEventListener("paste", aoColarNaPagina); ouvindo = true; }
  return () => {
    const i = pilha.lastIndexOf(handler);
    if (i >= 0) pilha.splice(i, 1);
    if (!pilha.length && ouvindo) { document.removeEventListener("paste", aoColarNaPagina); ouvindo = false; }
  };
}

/* `aoColar(link)` recebe o link limpo. `ativo` desliga o hook sem desmontar
   (ex.: o modal já passou da etapa do link).
   Devolve a função do botão "Colar" — no Android não existe Ctrl+V, então o
   botão lê a área de transferência. Devolve false se não tinha link lá. */
export function useColarLink(aoColar, { ativo = true } = {}) {
  // O que entra na pilha é um token estável, e não `aoColar` — quem chama
  // costuma passar arrow inline, que muda de identidade a cada render. Se a
  // inscrição mudasse junto, um re-render do campo de trás o jogaria pro topo
  // da pilha e ele roubaria o Ctrl+V do modal que está aberto na frente.
  const referencia = useRef(aoColar);
  referencia.current = aoColar;
  const estavel = useRef((link) => referencia.current?.(link)).current;

  useEffect(() => {
    if (!ativo) return;
    return registrar(estavel);
  }, [ativo, estavel]);

  return useCallback(async () => {
    try {
      const link = linkColado(await navigator.clipboard.readText());
      if (!link) return false;
      estavel(link);
      return true;
    } catch {
      // Sem permissão de área de transferência (ou navegador antigo): quem
      // chamou avisa pra colar no campo na mão.
      return false;
    }
  }, [estavel]);
}
